import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  SOCIAL_PLATFORM_ORDER,
  SocialPlatform,
  socialKeysNoticeName,
  type OptionalFeature,
  type ReconnectSocialDto,
  type SocialConnection,
} from '@mg/shared';
import { CheckCircle2, Info, ExternalLink, Link2, Plus, RefreshCw, SlidersHorizontal, X, AlertCircle } from 'lucide-react';
import { api, ApiRequestError, isSocialAppKeysNotSet } from '../lib/api';
import { useTenant } from '../auth/TenantContext';
import { useTenantKey } from '../lib/useTenantKey';
import { formatRelative } from '../lib/display';
import { PageHeader } from '../components/shell/PageHeader';
import { TestModeBanner } from '../features/testMode/TestModeBanner';
import { InboxSetupAlerts } from '../features/messages/InboxSetupAlerts';
import { lacksInstagramInbox } from '../features/messages/inboxAccess';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ErrorState, LoadingState } from '../components/ui/States';
import { useDialogs } from '../components/ui/DialogProvider';
import { useFieldId } from '../components/ui/useFieldId';
import { PLATFORM_META } from '../features/social/platforms';
import { ConnectModal, type ConnectTarget } from '../features/social/ConnectModal';
import { INSTAGRAM_NO_PROFESSIONAL_ACCOUNT } from '../features/social/connectCopy';
import { NotSetUpNotice } from '../features/social/NotSetUpNotice';
import { reconnectSetOf, SOCIAL_APP_KEYS_STATUS_KEY } from '../features/social/socialAppKeys';
import { useSocialAppKeysStatus, type SocialAppKeysGate } from '../features/social/useSocialAppKeysStatus';
import { FacebookPagePickerModal } from '../features/social/FacebookPagePickerModal';
import { LinkedInOrgPickerModal } from '../features/social/LinkedInOrgPickerModal';
import { InstagramAccountPickerModal } from '../features/social/InstagramAccountPickerModal';
import { ConnectionDetailsModal } from '../features/social/ConnectionDetailsModal';
import { expiresLabel, formatSignInEnd, signInEndingSoon } from '../features/social/signInExpiry';
import { reconnectNeeded } from '../features/social/reconnect';

/**
 * An optional feature shown as a chip on a connected account's card: what it is called, and what
 * the confirm says before "Turn on" sends the person back to the platform to approve it.
 */
interface FeatureChip {
  feature: OptionalFeature;
  label: string;
  title: string;
  body: string;
  continueLabel: string;
  /** Where "Show me how" leads, when there is a guide for it. */
  guide?: string;
}

/**
 * The chips, in the order a card shows them. A card shows the ones its connection has (the API
 * names them in `optionalFeatures`): a Facebook Page connects with posting only, and each of these
 * needs something added to the install's Facebook (or Threads) app in Site Integrations first -
 * asked for before that, Facebook refuses the whole sign-in ("Invalid Scopes"), so each is turned on
 * separately, when the app is ready.
 */
const FEATURE_CHIPS: FeatureChip[] = [
  {
    feature: 'messaging',
    label: 'Messenger inbox',
    title: 'Turn on the Messenger inbox',
    body: 'Read and reply to your Page\'s messages here. The Facebook app in Site Integrations needs the Messenger product first (on an app made with use cases: the "Engage with customers on Messenger from Meta" use case), or Facebook will show an "Invalid Scopes" error. Then you\'ll sign in to Facebook again to approve it.',
    continueLabel: 'Continue to Facebook',
    guide: '/guide/facebook-app',
  },
  {
    feature: 'engagement',
    label: 'Comments & mentions',
    title: 'Turn on comments & mentions',
    body: 'Read and reply to comments and mentions on your Page. First check the Facebook app in Site Integrations has the pages_read_user_content and pages_manage_engagement permissions (App Review → Permissions and Features; on an app made with use cases: its "Manage everything on your Page" use case). Then you\'ll sign in to Facebook again to approve them.',
    continueLabel: 'Continue to Facebook',
    guide: '/guide/facebook-app',
  },
  {
    feature: 'insights',
    label: 'Page analytics',
    title: 'Turn on Page analytics',
    body: 'Daily reach, views and reactions on the Analytics page. First check the Facebook app in Site Integrations has the read_insights permission (App Review → Permissions and Features; on an app made with use cases: its "Manage everything on your Page" use case). Then you\'ll sign in to Facebook again to approve it.',
    continueLabel: 'Continue to Facebook',
    guide: '/guide/facebook-app',
  },
  // Threads: reading the posts that @mention the account. (Publishing threads, its other optional
  // permission, keeps the "Enable threads" line of its own.)
  {
    feature: 'threadMentions',
    label: 'Mentions',
    title: 'Turn on Threads mentions',
    body: 'See posts that @mention this account in Messages. The Threads app in Site Integrations needs threads_manage_mentions in its "Access the Threads API" use case first. Then you\'ll sign in to Threads again to approve it. Until Meta approves that app for this permission, only mentions from people who are testers on it appear.',
    continueLabel: 'Continue to Threads',
  },
];

/**
 * Social Media Feeds (the Connections page) — connect the workspace's social
 * accounts so posts can be published to them and their messages, comments and
 * analytics can be read. Multiple discrete accounts per platform are supported.
 * Connections are per workspace.
 */
export function SocialConnectionsPage() {
  const { activeTenant } = useTenant();
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const { confirm, alert } = useDialogs();
  const [searchParams, setSearchParams] = useSearchParams();
  // Whether each Site Integrations credential set has app keys: what gates every action that starts a
  // sign-in. The page's one observer of it; the connect dialog takes it as a prop.
  const keys = useSocialAppKeysStatus();
  // What the connect dialog is open for (null = closed): a new connection, or a Reconnect.
  const [connectTarget, setConnectTarget] = useState<ConnectTarget | null>(null);
  // An Instagram sign-in through Facebook found no Instagram professional account to connect.
  const [igNoAccount, setIgNoAccount] = useState(false);
  // The pending Facebook connection id whose Page we're picking (null = closed).
  const [facebookPickerCid, setFacebookPickerCid] = useState<string | null>(null);
  // The pending LinkedIn (Community Management) connection id whose org we're picking.
  const [linkedinPickerCid, setLinkedinPickerCid] = useState<string | null>(null);
  // The pending Instagram-via-Facebook connection id whose account we're picking.
  const [instagramPickerCid, setInstagramPickerCid] = useState<string | null>(null);
  // The connection whose details modal is open (null = closed).
  const [detailsConn, setDetailsConn] = useState<SocialConnection | null>(null);

  // Handle the return from an OAuth round-trip:
  //   Facebook: ?fb=select&cid=…|denied|error   LinkedIn: ?li=connected|denied|error
  //   Generic (IG/Threads/X): ?oauth=connected&platform=…|denied|error
  const fbParam = searchParams.get('fb');
  const cidParam = searchParams.get('cid');
  const liParam = searchParams.get('li');
  const igParam = searchParams.get('ig');
  const oauthParam = searchParams.get('oauth');
  const oauthPlatformParam = searchParams.get('platform');
  const oauthReasonParam = searchParams.get('reason');
  const [fbNotice, setFbNotice] = useState<'denied' | 'error' | null>(null);
  const [liNotice, setLiNotice] = useState<'connected' | 'denied' | 'error' | null>(null);
  // Why a LinkedIn connect failed, as the server put it - e.g. a Reconnect that signed in as a
  // different Page.
  const [liReason, setLiReason] = useState<string | null>(null);
  const [oauthNotice, setOauthNotice] = useState<
    { kind: 'connected' | 'denied' | 'error'; label?: string; reason?: string } | null
  >(null);

  useEffect(() => {
    if (!fbParam && !liParam && !igParam && !oauthParam) return;
    if (igParam === 'select' && cidParam) {
      setInstagramPickerCid(cidParam);
    }
    if (fbParam === 'select') {
      if (cidParam) setFacebookPickerCid(cidParam);
    } else if (fbParam === 'denied') {
      setFbNotice('denied');
    } else if (fbParam === 'error') {
      setFbNotice('error');
    }
    if (liParam === 'orgselect') {
      if (cidParam) setLinkedinPickerCid(cidParam);
    } else if (liParam === 'connected') {
      setLiNotice('connected');
    } else if (liParam === 'denied') {
      setLiNotice('denied');
    } else if (liParam === 'error') {
      setLiNotice('error');
      setLiReason(oauthReasonParam);
    }
    if (oauthParam === 'connected' || oauthParam === 'denied' || oauthParam === 'error') {
      const key = (oauthPlatformParam ?? '').toUpperCase() as SocialPlatform;
      setOauthNotice({
        kind: oauthParam,
        label: PLATFORM_META[key]?.label,
        reason: oauthParam === 'error' ? (oauthReasonParam ?? undefined) : undefined,
      });
    }
    // Strip the round-trip params; component state drives the UI from here on.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('fb');
        next.delete('cid');
        next.delete('li');
        next.delete('ig');
        next.delete('oauth');
        next.delete('platform');
        next.delete('reason');
        return next;
      },
      { replace: true },
    );
  }, [
    fbParam,
    cidParam,
    liParam,
    igParam,
    oauthParam,
    oauthPlatformParam,
    oauthReasonParam,
    setSearchParams,
  ]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
  });

  const disconnect = useMutation({
    mutationFn: (connectionId: string) => api.disconnectSocial(connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['social-connections', tenantKey] });
    },
  });

  const connections = data ?? [];
  const connectedPlatforms = new Set(
    connections.filter((c) => c.connected).map((c) => c.platform),
  );
  const connectedCount = connectedPlatforms.size;
  const total = SOCIAL_PLATFORM_ORDER.length;
  const pct = total > 0 ? Math.round((connectedCount / total) * 100) : 0;

  // Connect and "Add Additional": the platform's connect dialog for a NEW connection, only while the
  // app keys it needs are there (the buttons are disabled otherwise; this is the same rule).
  const openConnect = (platform: SocialPlatform, opener: HTMLElement) => {
    if (keys.newConnectionState(platform) !== 'ready') return;
    setConnectTarget({ platform, reconnect: null, opener });
  };

  // Reconnect opens the platform's connect dialog; the server signs in with the install's Site
  // Integrations app for the account's credential set (an Instagram Login account keeps its own app,
  // so it reconnects directly, with no dialog). Only a saved connection can be reconnected - the
  // not-yet-connected placeholder card has no id.
  const onReconnect = (conn: SocialConnection, opener: HTMLElement) => {
    const set = reconnectSetOf(conn);
    if (set === null) {
      void reconnectNow(conn);
      return;
    }
    if (keys.stateOf(set) !== 'ready') return;
    setConnectTarget({ platform: conn.platform, reconnect: conn, opener });
  };

  // The reconnect itself, with no dialog: an Instagram Login account's Reconnect, and "Enable
  // threads", "Enable inbox" and each "Turn on", which also ask the platform for one more permission.
  // The server hands back the provider URL. A refusal because the app keys are missing (409) also asks
  // the key status again, so the page disables what can no longer start.
  const reconnectNow = async (conn: SocialConnection, dto?: ReconnectSocialDto) => {
    if (!conn.id) return;
    try {
      const { url } = await api.reconnectSocial(conn.id, dto);
      window.location.href = url;
    } catch (err) {
      if (isSocialAppKeysNotSet(err)) void queryClient.invalidateQueries({ queryKey: SOCIAL_APP_KEYS_STATUS_KEY });
      await alert({
        title: 'Could not start the reconnect',
        message: err instanceof ApiRequestError ? err.message : 'Please try again.',
      });
    }
  };

  // "Turn on" an optional feature: say what the install's Facebook (or Threads) app in Site
  // Integrations needs first, then reconnect asking for that feature as well. The account keeps
  // everything it already has.
  const turnOn = async (conn: SocialConnection, chip: FeatureChip) => {
    const agreed = await confirm({
      title: chip.title,
      message: (
        <>
          <p>{chip.body}</p>
          {chip.guide && (
            <p className="mt-3">
              <a
                href={chip.guide}
                target="_blank"
                rel="noreferrer"
                className="focus-ring inline-flex items-center gap-1 font-medium text-link hover:text-action-hover"
              >
                Show me how <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </p>
          )}
        </>
      ),
      confirmLabel: chip.continueLabel,
      cancelLabel: 'Cancel',
    });
    if (!agreed) return;
    const dto: ReconnectSocialDto = {};
    dto[chip.feature] = true;
    await reconnectNow(conn, dto);
  };

  return (
    <>
      <PageHeader
        title="Social Media Feeds"
        icon={Link2}
        description={`${activeTenant?.name ?? 'Workspace'} · Manage connected accounts`}
      />

      <TestModeBanner />

      {isLoading ? (
        <Card className="p-6">
          <LoadingState label="Loading connected accounts…" />
        </Card>
      ) : isError ? (
        <Card className="p-6">
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load accounts.'}
            onRetry={() => void refetch()}
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Progress banner: the graphite action fill, which turns light in the dark theme, so its
              text and ring use the action foreground with it. */}
          <div className="flex items-center justify-between rounded-2xl bg-action px-6 py-5 text-action-fg shadow-card">
            <div>
              <div className="text-2xl font-bold leading-none">
                {connectedCount} of {total}
              </div>
              <div className="mt-1 text-sm font-medium text-action-fg/85">Platforms Connected</div>
            </div>
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-action-fg/20 text-xl font-bold">
              {pct}%
            </div>
          </div>

          {/* Setup / health alerts (missing keys, reconnect needed) */}
          <InboxSetupAlerts variant="connections" />

          {/* Facebook OAuth return notices */}
          {fbNotice === 'denied' && (
            <FbNotice
              tone="info"
              message="Facebook connection was cancelled."
              onDismiss={() => setFbNotice(null)}
            />
          )}
          {fbNotice === 'error' && (
            <FbNotice
              tone="error"
              message="Couldn't connect to Facebook. Please try again."
              onDismiss={() => setFbNotice(null)}
            />
          )}

          {/* LinkedIn OAuth return notices */}
          {liNotice === 'connected' && (
            <FbNotice
              tone="success"
              message="LinkedIn account connected."
              onDismiss={() => setLiNotice(null)}
            />
          )}
          {liNotice === 'denied' && (
            <FbNotice
              tone="info"
              message="LinkedIn connection was cancelled."
              onDismiss={() => setLiNotice(null)}
            />
          )}
          {liNotice === 'error' && (
            <FbNotice
              tone="error"
              message={`Couldn't connect to LinkedIn.${liReason ? ` ${liReason}` : ' Please try again.'}`}
              onDismiss={() => setLiNotice(null)}
            />
          )}

          {/* Generic OAuth (Instagram / Threads / X) return notices */}
          {oauthNotice?.kind === 'connected' && (
            <FbNotice
              tone="success"
              message={`${oauthNotice.label ?? 'Account'} connected.`}
              onDismiss={() => setOauthNotice(null)}
            />
          )}
          {oauthNotice?.kind === 'denied' && (
            <FbNotice
              tone="info"
              message={`${oauthNotice.label ?? 'Social'} connection was cancelled.`}
              onDismiss={() => setOauthNotice(null)}
            />
          )}
          {oauthNotice?.kind === 'error' && (
            <FbNotice
              tone="error"
              message={`Couldn't connect${oauthNotice.label ? ` to ${oauthNotice.label}` : ''}.${
                oauthNotice.reason ? ` ${oauthNotice.reason}` : ' Please try again.'
              }`}
              onDismiss={() => setOauthNotice(null)}
            />
          )}

          {/* Instagram through Facebook: signed in, but no professional account to pick. */}
          {igNoAccount && (
            <FbNotice
              tone="error"
              message={INSTAGRAM_NO_PROFESSIONAL_ACCOUNT}
              onDismiss={() => setIgNoAccount(false)}
            />
          )}

          {/* One group per platform — each can hold multiple accounts. */}
          {SOCIAL_PLATFORM_ORDER.map((platform) => {
            const accounts = connections.filter((c) => c.platform === platform);
            return (
              <PlatformGroup
                key={platform}
                platform={platform}
                accounts={accounts}
                keys={keys}
                onConnect={(opener) => openConnect(platform, opener)}
                onFinish={(conn) => {
                  if (conn.platform === SocialPlatform.FACEBOOK) {
                    setFacebookPickerCid(conn.id ?? null);
                  } else if (conn.platform === SocialPlatform.LINKEDIN) {
                    setLinkedinPickerCid(conn.id ?? null);
                  } else if (conn.platform === SocialPlatform.INSTAGRAM) {
                    setInstagramPickerCid(conn.id ?? null);
                  }
                }}
                onDetails={(conn) => setDetailsConn(conn)}
                onReconnect={onReconnect}
                onReconnectWith={(conn, dto) => void reconnectNow(conn, dto)}
                onTurnOn={turnOn}
                onDisconnect={async (conn) => {
                  const label = conn.accountName ?? PLATFORM_META[conn.platform].label;
                  const waiting = conn.unpublishedPostCount ?? 0;
                  const message = conn.pending
                    ? `Cancel the ${label} connection? You're signed in but haven't finished — you'll need to sign in again to reconnect.`
                    : waiting > 0
                      ? `Disconnect ${label}? ${
                          waiting === 1
                            ? "1 post that hasn't been published yet is"
                            : `${waiting} posts that haven't been published yet are`
                        } set to publish through it. They keep this account: they'll publish once it's connected again, or you can choose another account for them.`
                      : `Disconnect ${label}?`;
                  if (
                    conn.id &&
                    (await confirm({
                      title: 'Disconnect account',
                      message,
                      confirmLabel: 'Disconnect',
                      danger: true,
                    }))
                  ) {
                    disconnect.mutate(conn.id);
                  }
                }}
                disconnectingId={disconnect.isPending ? (disconnect.variables ?? null) : null}
              />
            );
          })}

          {/* About */}
          <div className="flex gap-3 rounded-2xl border border-line bg-tint/70 px-5 py-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-tint-fg" aria-hidden />
            <div>
              <p className="text-sm font-semibold text-ink">About Social Media Connections</p>
              <p className="mt-1 text-sm text-ink-strong/90">
                Connecting your social media accounts lets you monitor activity and publish content
                directly from this platform. You can connect more than one account per platform, and
                disconnect any of them at any time.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mounted only while open, so every opening starts fresh (choice, start, error). */}
      {connectTarget && (
        <ConnectModal
          key={`${connectTarget.platform}:${connectTarget.reconnect?.id ?? 'new'}`}
          target={connectTarget}
          gate={keys}
          onClose={() => setConnectTarget(null)}
        />
      )}

      <FacebookPagePickerModal
        open={facebookPickerCid != null}
        tenantKey={tenantKey}
        connectionId={facebookPickerCid}
        onClose={() => setFacebookPickerCid(null)}
        onConnected={() => setFacebookPickerCid(null)}
      />

      <LinkedInOrgPickerModal
        open={linkedinPickerCid != null}
        tenantKey={tenantKey}
        connectionId={linkedinPickerCid}
        onClose={() => setLinkedinPickerCid(null)}
        onConnected={() => setLinkedinPickerCid(null)}
      />

      <InstagramAccountPickerModal
        open={instagramPickerCid != null}
        tenantKey={tenantKey}
        connectionId={instagramPickerCid}
        onClose={() => setInstagramPickerCid(null)}
        onConnected={() => setInstagramPickerCid(null)}
        // What Cancel does, plus the notice. Nothing is deleted: the attempt stays "Action needed", and
        // "Finish connecting" asks again.
        onNoAccounts={() => {
          setInstagramPickerCid(null);
          setIgNoAccount(true);
        }}
      />

      <ConnectionDetailsModal conn={detailsConn} onClose={() => setDetailsConn(null)} />
    </>
  );
}

function FbNotice({
  tone,
  message,
  onDismiss,
}: {
  tone: 'info' | 'success' | 'error';
  message: string;
  onDismiss: () => void;
}) {
  const styles = {
    info: { box: 'border-line bg-tint/70', text: 'text-ink-strong/90', btn: 'text-ink-faint hover:text-ink-muted' },
    success: { box: 'border-success/20 dark:border-success-dark/30 bg-success-tint/70 dark:bg-success-dark-tint/70', text: 'text-success dark:text-success-dark', btn: 'text-success dark:text-success-dark hover:text-success dark:hover:text-success-dark' },
    error: { box: 'border-red-100 dark:border-error-dark/30 bg-red-50/70 dark:bg-error-dark-tint/70', text: 'text-red-800 dark:text-error-dark', btn: 'text-red-400 dark:text-error-dark hover:text-red-600 dark:hover:text-error-dark' },
  }[tone];
  const Icon = tone === 'error' ? AlertCircle : tone === 'success' ? CheckCircle2 : Info;
  const iconColor =
    tone === 'error' ? 'text-red-500 dark:text-error-dark' : tone === 'success' ? 'text-success dark:text-success-dark' : 'text-tint-fg';
  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border px-5 py-4 ${styles.box}`}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconColor}`} aria-hidden />
      <p className={`flex-1 text-sm ${styles.text}`}>{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={`focus-ring -m-1 rounded-md p-1 ${styles.btn}`}
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

/** What a platform's group and its cards do with an account (see ConnectionCard). */
interface CardActions {
  /** A new connection on this platform: opens its connect dialog. `opener` takes focus back on close. */
  onConnect: (opener: HTMLElement) => void;
  onFinish: (conn: SocialConnection) => void;
  onDetails: (conn: SocialConnection) => void;
  onDisconnect: (conn: SocialConnection) => void;
  /**
   * Reconnect: opens the platform's connect dialog, which signs in with the install's Site Integrations
   * app for the account's credential set (an Instagram Login account keeps its own app and reconnects
   * at once). `opener` takes focus back on close.
   */
  onReconnect: (conn: SocialConnection, opener: HTMLElement) => void;
  /**
   * The same reconnect at once, with no dialog, also asking for more: `{ threadReplies: true }` is
   * "Enable threads", `{ inbox: true }` is "Enable inbox".
   */
  onReconnectWith: (conn: SocialConnection, dto: ReconnectSocialDto) => void;
  /** Turn on one of the connection's optional features (confirm first, then a reconnect). */
  onTurnOn: (conn: SocialConnection, chip: FeatureChip) => void;
}

/** All accounts for a single platform, plus the connect / add-another affordance. */
function PlatformGroup({
  platform,
  accounts,
  keys,
  disconnectingId,
  ...actions
}: CardActions & {
  platform: SocialPlatform;
  accounts: SocialConnection[];
  /** Whether each credential set has app keys. */
  keys: SocialAppKeysGate;
  disconnectingId: string | null;
}) {
  const meta = PLATFORM_META[platform];
  const groupNoticeId = useFieldId('social-keys-notice');
  // Whether a NEW connection on this platform can start (LinkedIn: while either type has keys).
  const newState = keys.newConnectionState(platform);

  // No accounts yet — a single "Connect <platform>" card (the placeholder).
  if (accounts.length === 0) {
    return (
      <ConnectionCard
        conn={{ platform, connected: false }}
        keys={keys}
        groupNoticeId={null}
        disconnecting={false}
        {...actions}
      />
    );
  }

  // Not set up: one notice for the whole group, which every action it blocks points at.
  const groupNotice = newState === 'missing';
  return (
    <div className="space-y-2">
      {accounts.map((conn) => (
        <ConnectionCard
          key={conn.id ?? conn.platform}
          conn={conn}
          keys={keys}
          groupNoticeId={groupNotice ? groupNoticeId : null}
          disconnecting={disconnectingId != null && disconnectingId === conn.id}
          {...actions}
        />
      ))}
      {groupNotice && <NotSetUpNotice row id={groupNoticeId} name={meta.label} />}
      {/* Add another discrete account for this platform: disabled while it could not start (and while
          that is not known yet), described by the notice when it is missing. */}
      <button
        type="button"
        disabled={newState !== 'ready'}
        aria-describedby={groupNotice ? groupNoticeId : undefined}
        onClick={(e) => actions.onConnect(e.currentTarget)}
        className="focus-ring flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-line-dashed bg-card/60 px-5 py-3 text-sm font-medium text-ink-label transition-colors enabled:hover:border-ink-ghost enabled:hover:bg-tint enabled:hover:text-tint-fg disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Plus className="h-4 w-4" aria-hidden /> Add Additional {meta.label} Account
      </button>
    </div>
  );
}

function ConnectionCard({
  conn,
  keys,
  groupNoticeId,
  disconnecting,
  onConnect,
  onFinish,
  onDetails,
  onDisconnect,
  onReconnect,
  onReconnectWith,
  onTurnOn,
}: CardActions & {
  conn: SocialConnection;
  /** Whether each credential set has app keys. */
  keys: SocialAppKeysGate;
  /** The id of the group's not-set-up notice while it is shown (null otherwise). */
  groupNoticeId: string | null;
  disconnecting: boolean;
}) {
  const meta = PLATFORM_META[conn.platform];
  const { Icon } = meta;
  const noticeId = useFieldId('social-keys-notice');
  // The placeholder's Connect: whether a NEW connection can start (LinkedIn: while either type can).
  const newState = keys.newConnectionState(conn.platform);
  // A connected account's Reconnect, "Enable threads" and "Turn on" sign in with the account's own
  // credential set. An Instagram Login account has none (it keeps its own app): never gated.
  const set = conn.connected ? reconnectSetOf(conn) : null;
  const signInState = keys.stateOf(set);
  // Missing keys are explained by the group's notice when it is shown, else by one on this card (a
  // LinkedIn account whose type has no keys while the other type has them).
  const cardNotice = set !== null && signInState === 'missing' && groupNoticeId === null;
  const blockedBy = signInState === 'missing' ? (groupNoticeId ?? noticeId) : undefined;
  // Authorized but no Page/Organization picked yet — prompt the user to finish.
  const isPending = !conn.connected && conn.pending === true;
  // Connected, but the stored token is dead: it hard-failed refresh (e.g. an X refresh token revoked
  // when the same account re-authorized the same app elsewhere), a platform refused a publish with it
  // as expired or revoked, or its sign-in has ended with nothing to renew it (the API works that out
  // from its end date) → prompt a reconnect.
  const needsReconnect = reconnectNeeded(conn);
  // Still working, but the sign-in ends within a week: say so while there is time to reconnect.
  // Null when it does not (or when the account already needs a Reconnect - the red state wins).
  const expiresInDays = signInEndingSoon(conn);
  // Connected to Threads without the permission that replying needs (the default connect does not
  // ask for it), so it cannot publish a thread. Threads only: the API sends `false` for the
  // platforms that have no threads at all, and there is nothing to enable on those.
  const threadsOff =
    conn.connected && conn.platform === SocialPlatform.THREADS && conn.canPublishThreads === false;
  // An Instagram account connected with Instagram Login that does not have both permissions the
  // Messages inbox needs: "Enable inbox" is a reconnect that also asks Instagram for them.
  const inboxOff = lacksInstagramInbox(conn);
  // The optional features this account has, as the API names them - each on or off (shown once it
  // is connected). An older answer without the field shows none, rather than every one as off.
  const featureStates = conn.optionalFeatures;
  const chips = FEATURE_CHIPS.filter((chip) => featureStates?.[chip.feature] !== undefined);
  const pickUnit =
    conn.platform === SocialPlatform.FACEBOOK
      ? 'Page'
      : conn.platform === SocialPlatform.INSTAGRAM
        ? 'Instagram account'
        : 'Company Page';

  return (
    <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-4">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${meta.tile}`}>
          <Icon className="h-6 w-6" aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-ink">{meta.label}</span>
            {needsReconnect ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-error-dark-tint px-2 py-0.5 text-xs font-medium text-red-700 dark:text-error-dark">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden /> Reconnect needed
              </span>
            ) : expiresInDays != null ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-warning-dark-tint px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-warning-dark">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {expiresLabel(expiresInDays)}
              </span>
            ) : conn.connected ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-success-tint dark:bg-success-dark-tint px-2 py-0.5 text-xs font-medium text-success dark:text-success-dark">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Connected
              </span>
            ) : isPending ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-warning-dark-tint px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-warning-dark">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden /> Action needed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-muted">
                <Info className="h-3.5 w-3.5" aria-hidden /> Not Connected
              </span>
            )}
          </div>
          {/* Not connected, and no new connection can start: say why, under the name. */}
          {!conn.connected && !isPending && newState === 'missing' && (
            <NotSetUpNotice className="mt-2" id={noticeId} name={meta.label} />
          )}
          {conn.connected ? (
            <div className="mt-1 min-w-0">
              <div className="truncate text-sm font-medium text-ink">{conn.accountName}</div>
              <div className="truncate text-sm text-ink-muted">{conn.accountHandle}</div>
              <div className="mt-0.5 text-xs text-ink-faint">
                Last synced {formatRelative(conn.lastSyncedAt ?? null)}
              </div>
              {needsReconnect && (
                <p className="mt-1 text-xs font-medium text-red-600 dark:text-error-dark">
                  Access expired — reconnect to publish to it again and to restore messages, comments
                  &amp; mentions.
                </p>
              )}
              {expiresInDays != null && conn.signInExpiresAt && (
                <p className="mt-1 text-xs font-medium text-amber-700 dark:text-warning-dark">
                  This sign-in ends on {formatSignInEnd(conn.signInExpiresAt)}. Reconnect before then
                  so scheduled posts keep publishing.
                </p>
              )}
              {threadsOff && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {/* Enable threads asks Threads for threads_manage_replies (FEATURE_SCOPES): the
                      Meta app has to offer it first, as the Mentions confirm says for its own. */}
                  <p className="text-xs text-ink-muted">
                    Thread publishing is off for this account. The Threads app in Site Integrations
                    needs threads_manage_replies in its &ldquo;Access the Threads API&rdquo; use case
                    first.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    disabled={signInState !== 'ready'}
                    aria-describedby={blockedBy}
                    onClick={() => onReconnectWith(conn, { threadReplies: true })}
                  >
                    Enable threads
                  </Button>
                </div>
              )}
              {/* Instagram Login only, which keeps its own app: never waits on Site Integrations. */}
              {inboxOff && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() => onReconnectWith(conn, { inbox: true })}
                  >
                    Enable inbox
                  </Button>
                </div>
              )}
              {chips.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <p className="text-xs text-ink-muted">Optional features</p>
                  {chips.map((chip) =>
                    featureStates?.[chip.feature] ? (
                      <span
                        key={chip.feature}
                        className="inline-flex shrink-0 items-center rounded-full bg-success-tint dark:bg-success-dark-tint px-2 py-0.5 text-xs font-medium text-success dark:text-success-dark"
                      >
                        {chip.label} ✓
                      </span>
                    ) : (
                      <span
                        key={chip.feature}
                        className="inline-flex shrink-0 items-center gap-1.5 text-xs text-ink-muted"
                      >
                        {chip.label}
                        <Button
                          variant="secondary"
                          size="sm"
                          className="shrink-0"
                          aria-label={`Turn on ${chip.label}`}
                          disabled={signInState !== 'ready'}
                          aria-describedby={blockedBy}
                          onClick={() => onTurnOn(conn, chip)}
                        >
                          Turn on
                        </Button>
                      </span>
                    ),
                  )}
                </div>
              )}
              {cardNotice && set !== null && (
                <NotSetUpNotice className="mt-2" id={noticeId} name={socialKeysNoticeName(set, conn.platform)} />
              )}
            </div>
          ) : isPending ? (
            <p className="mt-1 text-sm text-ink-muted">
              You&apos;re signed in to {meta.label} — choose which {pickUnit} to connect to finish.
            </p>
          ) : (
            <p className="mt-1 text-sm text-ink-muted">
              Connect your {meta.label} account to start monitoring content
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {conn.connected ? (
          <>
            {/* Always offered, not just when the token is known-dead: refreshing a stale
                connection shouldn't require noticing a badge first. Opens the platform's connect
                dialog; the server signs in with the install's Site Integrations app for the
                account's credential set (an Instagram Login account keeps its own app, so it
                reconnects directly). Disabled while that set has no keys. */}
            <Button
              variant={needsReconnect || expiresInDays != null ? 'primary' : 'secondary'}
              size="sm"
              disabled={signInState !== 'ready'}
              aria-describedby={blockedBy}
              onClick={(e) => onReconnect(conn, e.currentTarget)}
            >
              <RefreshCw className="h-4 w-4" aria-hidden /> Reconnect
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onDetails(conn)}>
              <SlidersHorizontal className="h-4 w-4" aria-hidden /> Details
            </Button>
            {conn.accountUrl && (
              <a href={conn.accountUrl} target="_blank" rel="noreferrer">
                <Button variant="secondary" size="sm">
                  <ExternalLink className="h-4 w-4" aria-hidden /> View Account
                </Button>
              </a>
            )}
            <Button
              variant="secondary"
              size="sm"
              loading={disconnecting}
              onClick={() => onDisconnect(conn)}
              className="border-red-200 dark:border-error-dark/30 text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
            >
              Disconnect
            </Button>
          </>
        ) : isPending ? (
          <>
            <Button onClick={() => conn.id && onFinish(conn)}>
              Finish connecting — choose a {pickUnit}
            </Button>
            <Button
              variant="secondary"
              loading={disconnecting}
              onClick={() => onDisconnect(conn)}
              className="border-red-200 dark:border-error-dark/30 text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
            >
              Disconnect
            </Button>
          </>
        ) : (
          <Button
            disabled={newState !== 'ready'}
            aria-describedby={newState === 'missing' ? noticeId : undefined}
            onClick={(e) => onConnect(e.currentTarget)}
          >
            Connect {meta.label}
          </Button>
        )}
      </div>
    </Card>
  );
}
